import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/Button";
import { ApiError, api } from "@/lib/api-client";

const STATUS_COPY: Record<string, { label: string; tone: "ok" | "warn" | "danger" }> = {
  trialing: { label: "Trial", tone: "ok" },
  active: { label: "Active", tone: "ok" },
  past_due: { label: "Payment overdue", tone: "warn" },
  cancelled: { label: "Cancelled", tone: "warn" },
  suspended: { label: "Suspended", tone: "danger" },
  deleted: { label: "Deleted", tone: "danger" },
};

function fmtDate(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

export function Subscription() {
  const { data, isLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: api.subscription,
    refetchOnWindowFocus: true,
  });

  const sub = data?.data;
  const copy = sub ? STATUS_COPY[sub.status] : null;

  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  /**
   * Everything self-serve about billing lives in Stripe's hosted portal, so we
   * fetch a one-time URL and hand the browser over rather than rebuilding card
   * entry, invoices and cancellation here.
   *
   * Two expected non-errors get their own wording: a customer who never went
   * through Checkout has no Stripe customer to open a portal for, and the
   * platform may have no Stripe key configured at all. Neither is a bug the
   * customer can do anything about, so both point at support.
   */
  async function openBillingPortal() {
    setPortalBusy(true);
    setPortalError(null);
    try {
      const { portal_url } = await api.billingPortal("/subscription");
      window.location.href = portal_url;
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      setPortalError(
        code === "no_stripe_customer"
          ? "This subscription was not set up through checkout, so there is no billing portal for it. Email support@rumio.world and we'll make the change for you."
          : code === "stripe_unconfigured"
            ? "Billing isn't available right now. Email support@rumio.world and we'll sort it out."
            : "Could not open the billing portal. Please try again, or email support@rumio.world.",
      );
      setPortalBusy(false);
    }
  }

  return (
    <Shell title="Subscription">
      {isLoading && <p className="text-sm" style={{ color: "var(--color-muted)" }}>Loading…</p>}

      {!isLoading && !sub && (
        <div className="space-y-4">
          <p className="text-sm" style={{ color: "var(--color-muted)" }}>
            You don't have an active subscription yet.
          </p>
          <Link to="/welcome">
            <Button>Pick a plan</Button>
          </Link>
        </div>
      )}

      {sub && (
        <div className="space-y-4">
          <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
              Current plan
            </p>
            <p className="mt-1 text-lg font-semibold capitalize">{sub.plan_slug}</p>
            {copy && (
              <p
                className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs"
                style={{
                  background:
                    copy.tone === "ok"
                      ? "rgba(80, 160, 100, 0.15)"
                      : copy.tone === "warn"
                      ? "rgba(220, 150, 40, 0.15)"
                      : "rgba(220, 38, 38, 0.15)",
                  color:
                    copy.tone === "ok"
                      ? "#15803d"
                      : copy.tone === "warn"
                      ? "#b45309"
                      : "#b91c1c",
                }}
              >
                {copy.label}
              </p>
            )}
          </div>

          <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
              Dates
            </p>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              {sub.status === "trialing" && (
                <>
                  <dt style={{ color: "var(--color-muted)" }}>Trial ends</dt>
                  <dd>{fmtDate(sub.trial_ends_at)}</dd>
                </>
              )}
              {sub.current_period_end && (
                <>
                  <dt style={{ color: "var(--color-muted)" }}>Next billing</dt>
                  <dd>{fmtDate(sub.current_period_end)}</dd>
                </>
              )}
              {sub.cancel_at_period_end && (
                <>
                  <dt style={{ color: "var(--color-muted)" }}>Cancels on</dt>
                  <dd>{fmtDate(sub.current_period_end)}</dd>
                </>
              )}
              {sub.status === "suspended" && sub.grace_period_ends_at && (
                <>
                  <dt style={{ color: "var(--color-muted)" }}>Data preserved until</dt>
                  <dd>{fmtDate(sub.grace_period_ends_at)}</dd>
                </>
              )}
              {sub.promo?.code && (
                <>
                  <dt style={{ color: "var(--color-muted)" }}>Promo code</dt>
                  <dd>
                    <span className="font-mono">{sub.promo.code}</span>
                    {sub.promo.percent_off != null && <> — {sub.promo.percent_off}% off your first year</>}
                  </dd>
                </>
              )}
            </dl>
          </div>

          {/* Only rendered once the platform reports purchased add-ons — a
              pre-bundle payload leaves this page exactly as it was. */}
          {sub.addons && sub.addons.length > 0 && (
            <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
              <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
                Partner products
              </p>
              <ul className="mt-2 space-y-2 text-sm">
                {sub.addons.map((a) => (
                  <li key={a.slug} className="flex items-start justify-between gap-3">
                    <span>
                      <span className="font-medium">{a.name}</span>
                      {a.intro_ends_at && (
                        <span className="mt-0.5 block text-xs" style={{ color: "var(--color-muted)" }}>
                          Intro pricing ends {fmtDate(a.intro_ends_at)}
                        </span>
                      )}
                    </span>
                    <span className="whitespace-nowrap text-xs" style={{ color: "var(--color-muted)" }}>
                      {STATUS_COPY[a.status]?.label ?? a.status}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs" style={{ color: "var(--color-muted)" }}>
                Need to add or remove a partner product? Contact support.
              </p>
            </div>
          )}

          <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-muted)" }}>
              Manage billing
            </p>
            <p className="mt-2 text-sm">
              Update your card, download invoices, and manage your subscription in Stripe's
              secure billing portal.
            </p>
            <Button className="mt-3" onClick={openBillingPortal} disabled={portalBusy}>
              {portalBusy ? "Opening…" : "Manage billing"}
            </Button>
            {portalError && (
              <p className="mt-2 text-xs" style={{ color: "var(--color-danger, #b91c1c)" }} role="alert">
                {portalError}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Link to="/dashboard">
              <Button variant="ghost">Back to dashboard</Button>
            </Link>
          </div>
        </div>
      )}
    </Shell>
  );
}
